import { Button } from '@ctw/shared/components/button';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { notify } from '@ctw/shared/components/toast';
import { useBuilderUsage } from '@ctw/shared/content/summarization/use-builder-usage';
import {
  type PatientSummaryStatus,
  usePatientSummaries,
} from '@ctw/shared/content/summarization/use-patient-summaries';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useFeatureFlag } from '@ctw/shared/context/feature-flag-provider';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { TIMER_PATIENT_SUMMARY_GENERATION_DURATION_IN_SECONDS } from '@ctw/shared/context/telemetry/timer-metrics';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { tw } from '@ctw/shared/utils/tailwind';
import { faExternalLink } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const SUMMARY_LIMIT_APPROACHING_THRESHOLD = 0.8 as const;

export interface ActiveSummaryJob {
  patientUpid: string;
  status: PatientSummaryStatus;
}

interface UserPatientSummarizationState {
  summariesAllotted: number;
  summariesUsed: number;
  summarizationLimitApproaching: boolean;
  summarizationLimitReached: boolean;
  showMetering: boolean;

  reloadUsage: () => Promise<void>;

  activeSummaryJob: ActiveSummaryJob | null;
  generateSummary: (patientUpid: string) => void;
  checkCachedSummary: (patientUpid: string) => Promise<void>;
}

const UserPatientSummarizationContext = createContext<UserPatientSummarizationState | undefined>(
  undefined,
);

export const UserPatientSummarizationProvider = ({ children }: PropsWithChildren) => {
  const { navigate, pathname } = useZuiContext();
  const telemetry = useTelemetry();
  const { requestContext } = useCTW();
  const { monthlyLimit, summariesUsed, billingTier, isLoading, refetch } = useBuilderUsage();
  const hasMeteringFeature = useFeatureFlag('summarization-free-usage-limit');
  const summaryResult = usePatientSummaries({ autoSubmit: false });

  const [activePatientUpid, setActivePatientUpid] = useState<string | null>(null);
  const prevStatusRef = useRef(summaryResult.status);

  const generateSummary = useCallback(
    (patientUpid: string) => {
      setActivePatientUpid(patientUpid);
      summaryResult.submitJob(patientUpid);
    },
    [summaryResult.submitJob],
  );

  const checkCachedSummary = useCallback(
    async (patientUpid: string) => {
      // Always update the active patient — this clears any prior job for a different patient.
      setActivePatientUpid(patientUpid);

      const cachedStatus = await summaryResult.checkCachedSummary(patientUpid);
      if (cachedStatus === 'completed') {
        // Sync the ref so the notification effect doesn't fire a toast for a pre-existing summary.
        prevStatusRef.current = 'completed';
      } else if (!cachedStatus) {
        // No cached job found — clear the active patient so the card shows the generate button.
        setActivePatientUpid(null);
      }
    },
    [summaryResult.checkCachedSummary],
  );

  // In the standalone app, stop polling when navigating away from the patient's pages.
  // Skipped in iframe contexts where the UPID is not in the URL (and you can't navigate away from the patient anyway).
  useEffect(() => {
    if (!requestContext.isInIframe && activePatientUpid && !pathname.includes(activePatientUpid)) {
      setActivePatientUpid(null);
      summaryResult.reset();
    }
  }, [requestContext.isInIframe, activePatientUpid, pathname, summaryResult.reset]);

  // Handle job completion and error notifications
  useEffect(() => {
    if (!activePatientUpid) {
      return;
    }

    if (prevStatusRef.current === 'running' && summaryResult.status === 'completed') {
      void refetch().then(() => {
        const durationWaitedInSeconds = telemetry.stopTimerMetric(
          'loading',
          TIMER_PATIENT_SUMMARY_GENERATION_DURATION_IN_SECONDS,
        );

        telemetry.trackInteraction('patient_summary_ready_toast_presented', {
          summaryJobId: summaryResult.summaryJobId,
          durationWaitedInSeconds,
        });

        notify({
          type: 'success',
          title: 'Your patient summary is ready to view!',
          body: (
            <div className={tw`flex items-center justify-start gap-3`}>
              <Button
                type="button"
                variant="primary"
                onClick={() => navigate('summary')}
                telemetryInteractionName="patient_summary_ready_toast_view_button_clicked"
                telemetryObfuscatedIdentifier="View Patient Summary"
                telemetryContext={{
                  summaryJobId: summaryResult.summaryJobId,
                }}
              >
                <div className={tw`flex items-center space-x-2`}>
                  <div className={tw`whitespace-nowrap text-sm`}>View Patient Summary</div>
                  <FontAwesomeIcon icon={faExternalLink} className={tw`h-4 w-4`} />
                </div>
              </Button>
            </div>
          ),
        });
      });
    }

    if (prevStatusRef.current === 'running' && summaryResult.status === 'error') {
      notify({ type: 'error', title: 'Unable to generate patient summary' });
      setActivePatientUpid(null);
    }

    if (prevStatusRef.current === 'running' && summaryResult.status === 'insufficient-data') {
      notify({
        type: 'info',
        title: "Patient data isn't ready yet",
        body: 'The patient history job may still be running — check back shortly.',
      });
      setActivePatientUpid(null);
    }

    prevStatusRef.current = summaryResult.status;
  }, [
    activePatientUpid,
    summaryResult.status,
    summaryResult.summaryJobId,
    refetch,
    navigate,
    telemetry,
  ]);

  const activeSummaryJob: ActiveSummaryJob | null = activePatientUpid
    ? { patientUpid: activePatientUpid, status: summaryResult.status }
    : null;

  const summarizationState = useMemo(() => {
    const summarizationLimitReached = summariesUsed >= monthlyLimit && monthlyLimit > 0;
    const summarizationLimitApproaching =
      !summarizationLimitReached &&
      monthlyLimit > 0 &&
      summariesUsed / monthlyLimit >= SUMMARY_LIMIT_APPROACHING_THRESHOLD;
    const showMetering = hasMeteringFeature && billingTier === 'free';

    return {
      summariesAllotted: monthlyLimit,
      summariesUsed,
      summarizationLimitApproaching,
      summarizationLimitReached,
      showMetering,
      reloadUsage: refetch,
      activeSummaryJob,
      generateSummary,
      checkCachedSummary,
    };
  }, [
    monthlyLimit,
    summariesUsed,
    billingTier,
    hasMeteringFeature,
    refetch,
    activeSummaryJob,
    generateSummary,
    checkCachedSummary,
  ]);

  return (
    <UserPatientSummarizationContext.Provider value={summarizationState}>
      {isLoading ? <LoadingSpinner centered={true} /> : children}
    </UserPatientSummarizationContext.Provider>
  );
};

interface MockUserPatientSummarizationProviderProps extends PropsWithChildren {
  summariesAllotted: number;
  summariesUsed: number;
  activeSummaryJob?: ActiveSummaryJob | null;
  showMetering?: boolean;
}

/** For use in stories and tests only. Provides static context values without API calls. */
export const MockUserPatientSummarizationProvider = ({
  summariesAllotted,
  summariesUsed,
  activeSummaryJob = null,
  showMetering = true,
  children,
}: MockUserPatientSummarizationProviderProps) => {
  const summarizationState = useMemo(() => {
    const summarizationLimitReached = summariesUsed >= summariesAllotted && summariesAllotted > 0;
    const summarizationLimitApproaching =
      !summarizationLimitReached &&
      summariesAllotted > 0 &&
      summariesUsed / summariesAllotted >= SUMMARY_LIMIT_APPROACHING_THRESHOLD;

    return {
      summariesAllotted,
      summariesUsed,
      summarizationLimitApproaching,
      summarizationLimitReached,
      showMetering,
      reloadUsage: () => Promise.resolve(),
      activeSummaryJob,
      generateSummary: () => {
        /* noop */
      },
      checkCachedSummary: () => Promise.resolve(),
    };
  }, [summariesAllotted, summariesUsed, activeSummaryJob, showMetering]);

  return (
    <UserPatientSummarizationContext.Provider value={summarizationState}>
      {children}
    </UserPatientSummarizationContext.Provider>
  );
};

export const useUserPatientSummarizationContext = () => {
  const context = useContext(UserPatientSummarizationContext);

  if (!context) {
    throw new Error(
      'useUserPatientSummarizationContext must be used within a UserPatientSummarizationProvider',
    );
  }

  return context;
};
